import React from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { CheckCircle2, PenLine, XCircle } from "lucide-react";
import { Ingredient, User } from "../types/Types";
import SearchableSelect from "../select/SearchableSelect";

interface DetailDataSignatureRowsProps {
  // "os" (default) — unchanged Ingredient-backed dropdown+PIN rows used by /os.
  // "rn" — /work-order/6020: Подготвил is a server-stamped read-only text row
  // (admin-only reset), Одобрил is a PIN signature row like OS but gated on
  // Подготвил existing. See CLAUDE.md §7b — same component, mode prop, not a copy.
  mode?: "os" | "rn";
  ingredient?: Ingredient | null;
  isCreating: boolean;
  isSigned?: boolean;
  preparedUserId?: string;
  setPreparedUserId?: (v: string) => void;
  preparedDate?: string;
  setPreparedDate?: (v: string) => void;
  approvedUserId?: string;
  setApprovedUserId?: (v: string) => void;
  approvedDate?: string;
  setApprovedDate?: (v: string) => void;
  usersList?: User[];
  setHasChanges?: (v: boolean) => void;
  setSignField: (v: "prepared" | "approved" | null) => void;
  onRemoveSignature: (field: "prepared" | "approved") => void;
  // mode="rn" only — Подготвил/Одобрил display data (server-stamped, not user-selected)
  isAdmin?: boolean;
  onResetPrepared?: () => void;
  preparedUserName?: string;
  approvedUserName?: string;
  approvedSignature?: string;
}


const DetailDataSignatureRows: React.FC<DetailDataSignatureRowsProps> = ({
  mode = "os",
  ingredient = null,
  isCreating,
  isSigned = false,
  preparedUserId = "",
  setPreparedUserId = () => {},
  preparedDate = "",
  setPreparedDate = () => {},
  approvedUserId = "",
  setApprovedUserId = () => {},
  approvedDate = "",
  setApprovedDate = () => {},
  usersList = [],
  setHasChanges = () => {},
  setSignField,
  onRemoveSignature,
  isAdmin = false,
  onResetPrepared,
  preparedUserName,
  approvedUserName,
  approvedSignature,
}) => {
  const { t } = useTranslation();
  const preparedSig = ingredient?.prepared?.signature;
  const approvedSig = ingredient?.approved?.signature;
  const userName = (u: User) =>
    `${u.firstName || ""} ${u.lastName || ""}`.trim() || (u as any).userName || (u as any).email || u._id;
  // Same searchable dropdown as the rest of the app (not the browser's native <select>).
  const renderUserSelect = (value: string, onPick: (v: string) => void) => (
    <SearchableSelect<User>
      label={false}
      size="spreadsheet"
      data={usersList}
      value={value}
      onChange={(v) => {
        onPick(v);
        setHasChanges(true);
      }}
      getItemId={(u) => u._id}
      getDisplayText={userName}
      placeholder={t("qualDoc.selectUser") || "Select user"}
      disabled={isSigned}
      plainWhenDisabled
    />
  );

  if (mode === "rn") {
    return (
      <>
        <tr>
          <th className="border border-gray-300 bg-gray-200 text-gray-700 font-semibold text-left text-[11px] h-[26px] px-2">Подготвил</th>
          <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
            <div className="flex items-center gap-1 h-6">
              {preparedUserName ? (
                <span className="text-[11px] text-gray-700 truncate">{preparedUserName} · {preparedDate}</span>
              ) : (
                <span className="text-[11px] text-gray-400">—</span>
              )}
              {isAdmin && preparedUserName && (
                <button
                  type="button"
                  onClick={onResetPrepared}
                  className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-red-400 hover:text-red-600 ml-auto"
                  title={t("common.remove") || "Remove"}
                >
                  <XCircle className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </td>
        </tr>
        <tr>
          <th className="border border-gray-300 bg-gray-200 text-gray-700 font-semibold text-left text-[11px] h-[26px] px-2">Одобрил</th>
          <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
            <div className="flex items-center gap-1 h-6">
              {approvedSignature ? (
                <>
                  <span className="text-[11px] text-gray-700 truncate">{approvedUserName} · {approvedDate}</span>
                  <img src={approvedSignature} alt="sig" className="h-4 max-w-5 object-contain" />
                  <button
                    type="button"
                    onClick={() => onRemoveSignature("approved")}
                    className="w-5 h-5 shrink-0 inline-flex items-center justify-center text-red-400 hover:text-red-600 ml-auto"
                    title={t("common.remove") || "Remove"}
                  >
                    <XCircle className="w-3.5 h-3.5" />
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  disabled={isCreating || !preparedUserName}
                  onClick={() => setSignField("approved")}
                  title={!preparedUserName ? t("qualDoc.mustBePreparedFirst") || "Must be prepared first" : t("qualDoc.signDocument") || "Sign"}
                  className="w-6 h-6 shrink-0 inline-flex items-center justify-center text-gray-500 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PenLine className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </td>
        </tr>
      </>
    );
  }

  return (
    <>
      <tr>
        <th className="border border-gray-300 bg-gray-200 text-gray-700 font-semibold text-left text-[11px] h-[26px] px-2">Подготвил</th>
        <td className="border border-gray-300 h-[26px] px-1 py-0.5">
          <div className="flex items-center gap-1">
            <div className="flex-1 min-w-0">
              {renderUserSelect(preparedUserId, setPreparedUserId)}
            </div>
            <button
              type="button"
              title={preparedDate ? t("qualDoc.clearPrepared") || "Clear" : t("qualDoc.markAsPrepared") || "Set today"}
              onClick={() => {
                if (preparedDate) {
                  setPreparedDate("");
                  setPreparedUserId("");
                } else {
                  setPreparedDate(moment().format("DD.MM.YYYY"));
                }
                setHasChanges(true);
              }}
              disabled={isSigned}
              className="w-6 h-6 shrink-0 inline-flex items-center justify-center text-gray-500 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {preparedDate ? <XCircle className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            </button>
            <button
              type="button"
              disabled={!ingredient || isCreating}
              onClick={() => setSignField("prepared")}
              className={`w-6 h-6 shrink-0 inline-flex items-center justify-center text-gray-500 hover:text-blue-600 ${
                preparedSig ? "disabled:cursor-default" : "disabled:opacity-50 disabled:cursor-not-allowed"
              }`}
            >
              {preparedSig ? <img src={preparedSig} alt="sig" className="h-4 max-w-5 object-contain" /> : <PenLine className="w-3.5 h-3.5" />}
            </button>
            {preparedSig && (
              <button
                type="button"
                onClick={() => onRemoveSignature("prepared")}
                className="w-6 h-6 shrink-0 inline-flex items-center justify-center text-red-400 hover:text-red-600"
                title={t("common.remove") || "Remove"}
              >
                <XCircle className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </td>
      </tr>
      <tr>
        <th className="border border-gray-300 bg-gray-200 text-gray-700 font-semibold text-left text-[11px] h-[26px] px-2">Одобрил</th>
        <td className="border border-gray-300 h-[26px] px-1 py-0.5">
          <div className="flex items-center gap-1">
            <div className="flex-1 min-w-0">
              {renderUserSelect(approvedUserId, setApprovedUserId)}
            </div>
            <button
              type="button"
              title={approvedDate ? t("qualDoc.clearApproved") || "Clear" : t("qualDoc.markAsApproved") || "Set today"}
              onClick={() => {
                if (approvedDate) {
                  setApprovedDate("");
                  setApprovedUserId("");
                } else {
                  setApprovedDate(moment().format("DD.MM.YYYY"));
                }
                setHasChanges(true);
              }}
              disabled={isSigned}
              className="w-6 h-6 shrink-0 inline-flex items-center justify-center text-gray-500 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {approvedDate ? <XCircle className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            </button>
            <button
              type="button"
              disabled={!ingredient || isCreating || !preparedSig}
              onClick={() => setSignField("approved")}
              title={!preparedSig ? t("qualDoc.mustBePreparedFirst") || "Must be prepared first" : ""}
              className={`w-6 h-6 shrink-0 inline-flex items-center justify-center text-gray-500 hover:text-blue-600 ${
                approvedSig ? "disabled:cursor-default" : "disabled:opacity-50 disabled:cursor-not-allowed"
              }`}
            >
              {approvedSig ? <img src={approvedSig} alt="sig" className="h-4 max-w-5 object-contain" /> : <PenLine className="w-3.5 h-3.5" />}
            </button>
            {approvedSig && (
              <button
                type="button"
                onClick={() => onRemoveSignature("approved")}
                className="w-6 h-6 shrink-0 inline-flex items-center justify-center text-red-400 hover:text-red-600"
                title={t("common.remove") || "Remove"}
              >
                <XCircle className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </td>
      </tr>
    </>
  );
};

export default DetailDataSignatureRows;
